import Navbar from "../../Navbar/NavbarComponent/Navbar";
import { useEffect, useMemo, useState } from "react";
import Table from "../../../shared/components/Table";
import {
  ACTION_LABEL,
  BUTTON_LABEL,
  icons,
  LABELS,
  SEARCH_DATA,
  toastError,
  toastSuccess,
} from "../../../shared/constants";
import Search from "../../../shared/components/Search";
import SimpleButton from "../../../shared/components/Button/Button";
import Filter from "../../Common/Filter/Filter";
import { useFilter } from "../../../shared/utils/hooks/useFilter";
import { convertToAppropriateType } from "../../../shared/helperFunc/convertToAppropriateType";
import { dateAndTimeConvert, dateConvert } from "../../../shared/helperFunc/dateConvert";
import { DEBOUNCE_VALUE } from "../../../shared/constants/resource";
import useDebounce from "../../../shared/utils/hooks/useDebounce";
import {
  useFabricThresholdGetQuery,
  useFabricThresholdUpdateMutation,
  useFabricWarningLogExportMutation,
  useFabricWarningLogListQuery,
} from "../../../Features/fabric/fabric-slice";
import CustomModal from "../../../shared/components/CustomModal";
import NumberBox from "../../../shared/components/NumberBox";
import { fabricSettingsValidation } from "../../../shared/validation/fabric-settings-validation";
import {
  FiSliders,
  FiSave,
} from "react-icons/fi";
import authProvider from "../../../shared/config/authProvider";

const FabricWarningLog = () => {
  const downloadPath = process.env.REACT_APP_BACKEND_URL + "/uploads/";

  const [sort, setSort] = useState({ ...SEARCH_DATA, limit: 50 });
  const [count, setCount] = useState();
  const [warningLogList, setWarningLogList]: any = useState([]);
  const [handleFilter] = useFilter(sort, setSort);
  const [showFilter, setShowFilter] = useState(false);
  const [filterCalled, setFilterCalled]: any = useState(false);
  const [filterSelectionError, setFilterSelectionError]: any = useState();
  const [filterValues, setFilterValues] = useState<any>({});
  const [defaultFilterValues, setDefaultFilterValues] = useState<any>({});
  const [searchKeyword, setSearchKeyword] = useState(sort.search);
  const debouncedSearchKeyword = useDebounce(searchKeyword, DEBOUNCE_VALUE);

  // Settings Modal State
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [threshold, setThreshold]: any = useState("");
  const [lastChangedBy, setLastChangedBy] = useState("");
  const [lastChangedAt, setLastChangedAt]: any = useState(null);
  const [dataAsOf, setDataAsOf]: any = useState(null);
  const [errors, setErrors]: any = useState({});

  const {
    data: thresholdData,
    isSuccess: thresholdSuccess,
    refetch: thresholdRefetch,
  } = useFabricThresholdGetQuery("", {
    refetchOnMountOrArgChange: true,
  });

  const [thresholdUpdate, { isLoading: thresholdUpdateLoading }] =
    useFabricThresholdUpdateMutation();

  useEffect(() => {
    if (thresholdSuccess && thresholdData) {
      setThreshold(String(thresholdData.item.threshold ?? ""));
      setLastChangedBy(thresholdData.item.lastChangedBy || "");
      setLastChangedAt(thresholdData.item.lastChangedAt || null);
      setDataAsOf(thresholdData.item.dataAsOf || null);
    }
  }, [thresholdData, thresholdSuccess]);

  const handleThresholdChange = (e: any) => {
    setThreshold(e.target.value);
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const handleSaveThreshold = async () => {
    const { formErrors, formValid } = fabricSettingsValidation({ threshold });
    if (formValid) {
      const currentUser = authProvider.getUserData()?.item;
      const currentUserName = currentUser?.name || currentUser?.username || "";
      const updatePayload: any = {
        threshold: Number(threshold),
      };
      if (currentUserName) {
        updatePayload.lastChangedBy = currentUserName;
      }
      const updateResponse = await thresholdUpdate(updatePayload)
        .unwrap()
        .catch((err: any) => {
          toastError(err.data?.errors[0]?.msg);
        });
      if (updateResponse && updateResponse?.status) {
        toastSuccess(updateResponse.message);
        if (updateResponse.item?.lastChangedBy) {
          setLastChangedBy(updateResponse.item.lastChangedBy);
        }
        if (updateResponse.item?.lastChangedAt) {
          setLastChangedAt(updateResponse.item.lastChangedAt);
        }
        thresholdRefetch();
        warningLogRefetch();
        setShowSettingsModal(false);
      }
    } else {
      setErrors(formErrors);
    }
  };

  const queryArgs = useMemo(() => {
    return {
      ...sort,
      search: debouncedSearchKeyword,
    };
  }, [debouncedSearchKeyword, sort]);

  const {
    data,
    isSuccess,
    isFetching: warningLogFetching,
    refetch: warningLogRefetch,
  } = useFabricWarningLogListQuery(queryArgs, {
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
  });

  const [warningLogExport] = useFabricWarningLogExportMutation();

  useEffect(() => {
    if (isSuccess) {
      setWarningLogList(data.list);
      setCount(data.count);
    }
  }, [data, isSuccess]);

  const handleApplyFilter = () => {
    handleFilter();
    setFilterCalled(!filterCalled);
    setSort({ ...sort, filter: filterValues, filterFlag: true, skip: 0 });
    setShowFilter(!showFilter);
  };

  const handleCallFilter = () => {
    setShowFilter(!showFilter);
  };

  const resetFilter = () => {
    setFilterValues(defaultFilterValues);
    setSort({ ...sort, filter: defaultFilterValues, filterFlag: false });
    setFilterCalled(!filterCalled);
    setFilterSelectionError("");
    setShowFilter(!showFilter);
  };

  const closeFilter = () => {
    setFilterValues(defaultFilterValues);
    setFilterCalled(!filterCalled);
    setFilterSelectionError("");
    setShowFilter(false);
  };

  const handleSearch = () => {
    setSort({ ...sort, skip: 0 });
  };

  const handleSearchTextChange = (e: any) => {
    setSearchKeyword(e.target.value);
    setSort({
      ...sort,
      [e.target.name]: e.target.value,
      skip: 0,
    });
  };

  const handleKeyDown = (e: any) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleSelectionSearch = (item: any, name: any) => {
    if (item?.value) {
      setFilterValues({
        ...filterValues,
        [name]: item.value,
      });
    } else {
      let updatedFilter: any = { ...filterValues };
      delete updatedFilter[name];
      setFilterValues(updatedFilter);
    }
    setFilterSelectionError("");
  };

  const handleSelect = (item: any, e: any, type: string) => {
    let selectedValue = convertToAppropriateType(item?.value, type);

    if (item?.value) {
      setFilterValues({
        ...filterValues,
        [e.target.name]: selectedValue,
      });
    } else {
      let updatedFilter: any = { ...filterValues };
      delete updatedFilter[e.name];
      setFilterValues(updatedFilter);
    }
    setFilterSelectionError("");
  };

  const handleDate = (dates: any, filterName: any) => {
    const [start, end] = dates;

    const fromDate = start ? new Date(start).toISOString() : null;
    let toDate = null;
    if (end) {
      var datestring = new Date(end).toISOString().split("T")[0];
      toDate = new Date(datestring + "T23:59:59.000+05:30").toISOString();
    }

    setFilterValues({
      ...filterValues,
      [filterName]: { from: fromDate, to: toDate },
    });
  };

  const handleCsvExport = async () => {
    const exportResponse = await warningLogExport(sort)
      .unwrap()
      .catch((err: any) => {
        toastError(err.data?.errors[0]?.msg);
      });
    if (exportResponse && exportResponse?.status) {
      let a = document.createElement("a");
      a.href = downloadPath + exportResponse.item;
      a.download = exportResponse.item;
      a.click();
      toastSuccess(exportResponse.message);
    }
  };

  const filterContent = (
    handleSelect: any,
    handleSelectionSearch: any,
    handleDate: any
  ) => {
    return [
      {
        label: LABELS.STORE_NAME_TEXT,
        name: ACTION_LABEL.STORE,
        elementType: "selectSearch",
        action: handleSelectionSearch,
        type: "",
        operator: "is from",
        isSearch: true,
      },
      {
        label: LABELS.USER_DECISION_TEXT,
        name: ACTION_LABEL.USER_DECISION,
        elementType: "select",
        action: handleSelect,
        type: "",
        operator: "is from",
      },
      {
        label: LABELS.CREATED_AT_TEXT,
        name: ACTION_LABEL.CREATED_AT,
        elementType: "createdAt",
        action: handleDate,
        type: "",
        operator: "is from",
      },
    ];
  };

  const columns = useMemo(
    () => [
      {
        header: LABELS.DOCUMENT_NO_TEXT,
        accessorKey: ACTION_LABEL.DOCUMENT_NO,
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.FABRIC_CODE_TEXT,
        accessorKey: ACTION_LABEL.FABRIC_CODE,
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.AVAILABLE_STOCK_TEXT,
        accessorKey: ACTION_LABEL.AVAILABLE_STOCK,
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.THRESHOLD_TEXT,
        accessorKey: ACTION_LABEL.THRESHOLD,
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.USER_DECISION_TEXT,
        cell: (row: any) => {
          const decision = row?.row?.original?.userDecision;
          if (!decision) return "";
          const formatted =
            decision.charAt(0).toUpperCase() + decision.slice(1).toLowerCase();

          const isContinued = decision.toLowerCase() === "continued";
          const isCancelled = decision.toLowerCase() === "cancelled";
          const statusClass = isContinued
            ? "status text-success"
            : isCancelled
            ? "status text-danger"
            : "status text-pending";

          return <div className={statusClass}>{formatted}</div>;
        },
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.STORE_NAME_TEXT,
        cell: (row: any) => {
          return <>{row?.row?.original?.storeId?.name || ""}</>;
        },
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.DECIDED_AT_TEXT,
        cell: (row: any) => {
          return <>{dateAndTimeConvert(row?.row?.original?.decidedAt)}</>;
        },
        isShow: true,
        enableSorting: false,
      },
    ],
    []
  );

  return (
    <>
      <div className="full-height">
        <Navbar title={LABELS.FABRIC_WARNING_LOG_TEXT} />
        <main className="main-content">
          <div className="pt-3">
            <div className="container-fluid">
              <div className="row table-top-actions">
                <div className="col-12 col-md-12 d-flex justify-content-end">
                  <div className="d-flex align-items-center row w-100 mx-m-0">
                    <Search
                      placeholder={LABELS.SEARCH_TEXT}
                      name={ACTION_LABEL.SEARCH}
                      value={sort.search}
                      onChange={handleSearchTextChange}
                      onKeyDown={handleKeyDown}
                      onClick={handleSearch}
                    />

                    <SimpleButton
                      className="me-2 btn theme-btn theme-btn-sm btn-inverse w-auto d-inline-flex align-items-center gap-1.5"
                      onClick={() => setShowSettingsModal(true)}
                      text={
                        <span className="d-inline-flex align-items-center gap-1">
                          <FiSliders size={14} className="me-1" />
                          <span>Threshold Settings</span>
                        </span>
                      }
                    />

                    <SimpleButton
                      className={
                        "me-2 btn theme-btn theme-btn-sm btn-inverse w-auto"
                      }
                      onClick={handleCsvExport}
                      text={BUTTON_LABEL.EXPORT}
                    />

                    <SimpleButton
                      className={
                        "me-2 btn theme-btn theme-btn-sm btn-inverse filter-btn"
                      }
                      btnIcon={icons.ICON_FILTER}
                      btnIconClass={"me-2 d-inline-block"}
                      onClick={handleCallFilter}
                    />
                  </div>
                </div>
              </div>

              <div className="card custom-card card-full mt-3">
                <div className="card-body p-0">
                  <Table
                    data={warningLogList}
                    columns={columns}
                    sort={sort}
                    setSort={setSort}
                    coloumnDrop={true}
                    count={count}
                    isFetching={warningLogFetching}
                  />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      <CustomModal
        show={showSettingsModal}
        close={() => setShowSettingsModal(false)}
        size="md"
        message=""
        modalTitle={LABELS.FABRIC_STOCK_SETTINGS_TEXT}
        modalButton={[
          {
            text: BUTTON_LABEL.CANCEL,
            action: () => setShowSettingsModal(false),
            className: "btn theme-btn btn-inverse me-2",
          },
          {
            text: (
              <span className="d-inline-flex align-items-center gap-1">
                <FiSave size={14} />
                <span>{BUTTON_LABEL.SAVE}</span>
              </span>
            ),
            action: handleSaveThreshold,
            className: "btn theme-btn",
            isLoading: thresholdUpdateLoading,
          },
        ]}
        fullscreen={undefined}
        className="fabric-settings-modal"
        centerClass=""
        modalTitleIcon=""
        footerClass="d-flex justify-content-end p-3 border-top"
      >
        <div className="p-2">
          <div className="mb-3">
            <div className="form-group value-field">
              <NumberBox
                name={ACTION_LABEL.THRESHOLD}
                placeholder={LABELS.THRESHOLD_TEXT}
                id={LABELS.THRESHOLD_TEXT}
                label={LABELS.THRESHOLD_TEXT}
                onChange={handleThresholdChange}
                value={threshold}
                errorMessage={errors.threshold || null}
                min={1}
                required
              />
            </div>
          </div>

          {(lastChangedBy || lastChangedAt) && (
            <div className="mb-2">
              <p className="text-muted mb-0 font-12">
                {`${LABELS.LAST_CHANGED_BY_TEXT} ${
                  lastChangedBy || "-"
                } ${LABELS.LAST_CHANGED_AT_TEXT} ${
                  lastChangedAt ? dateConvert(lastChangedAt) : "-"
                }`}
              </p>
            </div>
          )}
        </div>
      </CustomModal>

      <Filter
        sort={filterValues}
        filterSelectionError={filterSelectionError}
        filterObject={filterContent(handleSelect, handleSelectionSearch, handleDate)}
        show={showFilter}
        onClose={closeFilter}
        handleReset={resetFilter}
        handleApply={handleApplyFilter}
      />
    </>
  );
};

export default FabricWarningLog;

